import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  FormField,
  Input,
} from '@bookaburra/ui';
import { CheckCircle2, Mail, XCircle } from 'lucide-react';
import { authClient } from '../auth/client';
import { AppHeader } from '../components/AppHeader';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { useDocumentTitle } from '../lib/use-document-title';

interface SignUpSearch {
  /** Pre-fill the email — used by the accept-invite page so the
   *  invited address auto-populates the signup form. */
  email?: string;
  /** Where to send the user after a successful auto sign-in. */
  redirect?: string;
  /** Subscription plan picked from the marketing pricing page. Stashed
   *  in localStorage so the onboarding page can read it after email
   *  verification. */
  plan?: string;
}

export const Route = createFileRoute('/signup')({
  validateSearch: (search: Record<string, unknown>): SignUpSearch => ({
    email: typeof search.email === 'string' ? search.email : undefined,
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    plan: typeof search.plan === 'string' ? search.plan : undefined,
  }),
  component: SignUpPage,
});

/** Key used to stash the chosen plan between /signup and /onboarding. */
export const PENDING_PLAN_KEY = 'bookaburra:pending-plan';

const MIN_PASSWORD_LEN = 8;

function SignUpPage() {
  useDocumentTitle('Create account');
  const navigate = useNavigate();
  const {
    email: prefilledEmail,
    redirect: postSignupRedirect,
    plan: chosenPlan,
  } = Route.useSearch();
  const { data: session, isPending: sessionPending } = authClient.useSession();

  // Already signed in? Send them somewhere useful instead of showing a
  // signup form they don't need. Honours ?redirect=… for same-origin
  // paths; otherwise falls back to /.
  useEffect(() => {
    if (sessionPending || !session?.user) return;
    const safe =
      typeof postSignupRedirect === 'string' &&
      postSignupRedirect.startsWith('/') &&
      !postSignupRedirect.startsWith('//')
        ? postSignupRedirect
        : '/';
    navigate({ to: safe });
  }, [sessionPending, session, postSignupRedirect, navigate]);

  // Stash the chosen plan from the marketing page so /onboarding can
  // read it after email verification. Only writes when present — never
  // clears an existing value (so a stale visit to /signup without
  // ?plan= doesn't wipe a plan the user already selected).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (chosenPlan) {
      try {
        window.localStorage.setItem(PENDING_PLAN_KEY, chosenPlan);
      } catch {
        // localStorage can throw in private mode — fall through; the
        // user just won't have plan pre-selected on /onboarding.
      }
    }
  }, [chosenPlan]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState(prefilledEmail ?? '');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Set after a signup submit. Switches the page to the "Check your
   *  email" success screen. */
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  /**
   * The "Didn't get the email?" block is hidden for the first 30 seconds
   * after a successful submit. People click resend reflexively before
   * the original email has even left our queue — which sends a fresh
   * email (with a new token), wastes the first one, and trains people
   * to expect the wrong UX. Holding the resend control back gives the
   * real email a chance to arrive.
   */
  const [resendReady, setResendReady] = useState(false);
  useEffect(() => {
    if (!submittedEmail) {
      setResendReady(false);
      return;
    }
    setResendReady(false);
    const t = setTimeout(() => setResendReady(true), 30_000);
    return () => clearTimeout(t);
  }, [submittedEmail]);

  /** Resend state used by the success screen's "Resend verification
   *  email" button. */
  const [resending, setResending] = useState(false);
  const [resendInfo, setResendInfo] = useState<
    { kind: 'sent' | 'error'; message?: string } | null
  >(null);

  const passwordError =
    password && password.length < MIN_PASSWORD_LEN
      ? `Password must be at least ${MIN_PASSWORD_LEN} characters.`
      : undefined;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (passwordError) return;
    if (!turnstileToken) {
      setError('Please complete the bot-check below.');
      return;
    }
    setError(null);
    setSubmitting(true);
    const trimmed = email.trim();
    // Where Better Auth's verify-email handler should send the user
    // after they click the link in the verification email. Default
    // is the home page with a "verified" banner; if the signup came
    // from /accept-invite (carries `?redirect=/accept-invite?token=…`),
    // bounce the user straight back so they can accept the invite
    // without an extra click.
    const verifyCallback = postSignupRedirect && postSignupRedirect.startsWith('/')
      ? `${window.location.origin}${postSignupRedirect}`
      : `${window.location.origin}/?verified=1`;
    const result = await authClient.signUp.email(
      {
        name: name.trim(),
        email: trimmed,
        password,
        // Must be absolute. Better Auth defaults to "/" which its own
        // verify-email handler rejects via originCheck, causing a 404
        // when the user clicks the link in the verification email.
        callbackURL: verifyCallback,
      },
      {
        // Turnstile token is verified by the Worker before Better Auth
        // sees the request (see apps/web/src/worker.ts).
        headers: { 'x-turnstile-token': turnstileToken },
      },
    );
    if (result.error) {
      setSubmitting(false);
      setError(result.error.message ?? 'Could not create your account.');
      return;
    }
    // Better Auth returns success even for duplicate emails when
    // requireEmailVerification is enabled (anti-enumeration). To tell
    // a genuine new signup from a returning verified user, try
    // signing in with the same credentials:
    //   - verified + correct password → land them straight in
    //   - unverified → SIGN_IN_NOT_VERIFIED → show the resend screen
    //   - wrong password → fall through to the resend screen so a
    //     fresh signup still works
    const signIn = await authClient.signIn.email({
      email: trimmed,
      password,
    });
    setSubmitting(false);
    if (signIn.data?.user && !signIn.error) {
      // Honour `?redirect=` if one was supplied (e.g. the accept-invite
      // page sending the user back to itself). Otherwise drop them on
      // the home page.
      if (postSignupRedirect && postSignupRedirect.startsWith('/')) {
        window.location.assign(postSignupRedirect);
        return;
      }
      navigate({ to: '/' });
      return;
    }
    setSubmittedEmail(trimmed);
  }

  async function onResend() {
    if (!submittedEmail || resending) return;
    setResending(true);
    setResendInfo(null);
    const result = await authClient.sendVerificationEmail({
      email: submittedEmail,
      // `?verified=1` tells the home route to render a success banner
// after Better Auth redirects here post-verify.
callbackURL: `${window.location.origin}/?verified=1`,
    });
    setResending(false);
    if (result.error) {
      setResendInfo({
        kind: 'error',
        message: result.error.message ?? 'Could not send the email.',
      });
      return;
    }
    // Better Auth's send-verification-email always returns success
    // (anti-enumeration). If the email is genuine and unverified, the
    // email is in flight; if already verified or unknown, nothing is
    // sent. Either way we acknowledge.
    setResendInfo({ kind: 'sent' });
  }

  // Avoid a form-flash before the signed-in redirect runs.
  if (sessionPending || session?.user) {
    return <div className="min-h-dvh bg-background" aria-busy="true" />;
  }

  if (submittedEmail) {
    return (
      <div className="min-h-dvh bg-background">
        <AppHeader />
        <main className="mx-auto flex max-w-md flex-col px-6 py-12 sm:py-16">
        <Alert variant="success">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Check your email</AlertTitle>
          <AlertDescription>
            We&rsquo;ve sent a verification link to{' '}
            <span className="font-medium">{submittedEmail}</span>. Click the link
            to finish setting up your account — you&rsquo;ll be signed in
            automatically.
          </AlertDescription>
        </Alert>

        {resendReady ? (
          <div className="mt-6 space-y-3 rounded-2xl border border-brand-sage/15 bg-card p-5 shadow-brand">
            <p className="text-sm text-muted-foreground">
              Didn&rsquo;t get the email? Check your spam folder, or resend the
              link below.
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              loading={resending}
              onClick={onResend}
              leadingIcon={<Mail className="h-4 w-4" />}
            >
              Resend verification email
            </Button>
            {resendInfo?.kind === 'sent' ? (
              <p className="text-center text-sm text-muted-foreground">
                Sent. Check your inbox in a minute.
              </p>
            ) : null}
            {resendInfo?.kind === 'error' ? (
              <p className="text-center text-sm text-destructive">
                {resendInfo.message}
              </p>
            ) : null}
          </div>
        ) : null}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already verified?{' '}
          <Link
            to="/signin"
            search={{ redirect: '/' }}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Sign in →
          </Link>
        </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto flex max-w-md flex-col px-6 py-12 sm:py-16">
        <header className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Already have one?{' '}
            <Link to="/signin" className="font-medium text-primary underline-offset-4 hover:underline">
              Sign in
            </Link>
            .
          </p>
        </header>

      <form onSubmit={onSubmit} className="space-y-5 rounded-2xl border border-brand-sage/15 bg-card p-8 shadow-brand">
        <FormField label="Your name" required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              autoComplete="name"
              required
              invalid={invalid}
              aria-describedby={describedBy}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sarah Mitchell"
            />
          )}
        </FormField>
        <FormField label="Email" required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              required
              invalid={invalid}
              aria-describedby={describedBy}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sarah@example.com.au"
            />
          )}
        </FormField>
        <FormField
          label="Password"
          required
          description={`At least ${MIN_PASSWORD_LEN} characters.`}
          error={passwordError}
        >
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LEN}
              invalid={invalid}
              aria-describedby={describedBy}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </FormField>

        <TurnstileWidget action="signup" onToken={setTurnstileToken} />

        {error ? (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Couldn&rsquo;t create the account</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Button
          type="submit"
          className="w-full"
          loading={submitting}
          disabled={
            !name ||
            !email ||
            !password ||
            !turnstileToken ||
            Boolean(passwordError)
          }
        >
          Create account
        </Button>

        <p className="text-xs text-muted-foreground">
          By creating an account you agree to receive transactional emails
          related to your bookings. We don&rsquo;t send marketing email
          without a separate opt-in.
        </p>
      </form>
      </main>
    </div>
  );
}
